// Collect focusable elements inside the modal for focus trapping.
const detailsModalGetFocusableElements = (container) =>
  Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );

// Holds active focus trap listeners so they can be removed cleanly.
const detailsModalTrapHandlers = {};

// Remove focus trap listeners and optionally restore focus.
function detailsModalRemoveTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', detailsModalTrapHandlers.focusin);
  document.removeEventListener('focusout', detailsModalTrapHandlers.focusout);
  document.removeEventListener('keydown', detailsModalTrapHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

// Trap focus within the modal and optionally focus an initial element.
function detailsModalTrapFocus(container, elementToFocus = container) {
  const elements = detailsModalGetFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  detailsModalRemoveTrapFocus();

  // Only arm keydown handling while focus is within the modal bounds.
  detailsModalTrapHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', detailsModalTrapHandlers.keydown);
  };

  detailsModalTrapHandlers.focusout = () => {
    document.removeEventListener('keydown', detailsModalTrapHandlers.keydown);
  };

  // Loop focus from last to first and first to last on Tab/Shift+Tab.
  detailsModalTrapHandlers.keydown = (event) => {
    if (event.code.toUpperCase() !== 'TAB') return;

    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', detailsModalTrapHandlers.focusout);
  document.addEventListener('focusin', detailsModalTrapHandlers.focusin);

  elementToFocus.focus();

  // Select all text in focused input for quick editing.
  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}


if (!customElements.get('details-modal')) {
  
  class DetailsModal extends HTMLElement {
    constructor() {
      super();
      this.detailsContainer = this.querySelector('details');
      this.summaryToggle = this.querySelector('summary');

      // Close on Escape, toggle on summary click, and close on close button click.
      this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
      this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
      this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));

      // Make summary act like a button for assistive tech.
      this.summaryToggle.setAttribute('role', 'button');
    }

    isOpen() {
      return this.detailsContainer.hasAttribute('open');
    }

    onSummaryClick(event) {
      event.preventDefault();
      event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
    }

    onBodyClick(event) {
      // Close if clicking outside or on the overlay.
      if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
    }

    open(event) {
      this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
      event.target.closest('details').setAttribute('open', true);
      document.body.addEventListener('click', this.onBodyClickEvent);
      document.body.classList.add('overflow-hidden');

      // Trap focus inside the modal, preferring the search input.
      detailsModalTrapFocus(
        this.detailsContainer.querySelector('[tabindex="-1"]'),
        this.detailsContainer.querySelector('input:not([type="hidden"])')
      );
    }

    close(focusToggle = true) {
      // Restore focus to the summary toggle unless suppressed.
      detailsModalRemoveTrapFocus(focusToggle ? this.summaryToggle : null);
      this.detailsContainer.removeAttribute('open');
      document.body.removeEventListener('click', this.onBodyClickEvent);
      document.body.classList.remove('overflow-hidden');
    }
  }

  customElements.define('details-modal', DetailsModal);
}
